/**
 * Custom hook to determine case transfer permissions
 */
import { useState, useEffect, useContext, useCallback } from 'react';
import { coderRedistributionViews } from '../components/organisms/workItemsTable';
import { fetchCaseTransferPermissionsConfigOptions } from '../utils/api';
import { policiesContext } from '../utils/common';

export interface UseCaseTransferPermissionsConfigReturn {
    hasCaseTransferPermission: boolean;
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
    canShowCaseTransferOption: (activeView: string) => boolean; 
}

export function useCaseTransferPermissionsConfig(): UseCaseTransferPermissionsConfigReturn {
    const [hasCaseTransferPermission, setHasCaseTransferPermission] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { userContext, roles } = useContext(policiesContext);
    


    const loadFilterOptions = useCallback(async () => {
        if (!userContext?.activeContext?.tenantId || !userContext?.serviceLine?.name) {
            console.warn('[useCaseTransferPermissionsConfig] Missing tenantId or serviceLine in userContext');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const options = await fetchCaseTransferPermissionsConfigOptions(
                userContext.activeContext.tenantId,
                userContext.serviceLine.name
            );

            const availableRoles = options?.availableRoles || [];

            let hasPermission = false;

            // Old logic (API-driven)
            for (const role of roles || []) {
                const match = availableRoles.find(
                    (r: any) => r.role_name === role && r.transferCase === true
                );
                if (match) {
                    hasPermission = true;
                    break;
                }
            }

            setHasCaseTransferPermission(hasPermission);

        } catch (err) {
            const errorMessage =
                err instanceof Error ? err.message : 'Failed to load user assignment permission configuration';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [
        userContext?.activeContext?.tenantId,
        userContext?.serviceLine?.name,
        roles
    ]);

    useEffect(() => {
        loadFilterOptions();
    }, [loadFilterOptions]);

    const canShowCaseTransferOption = (activeView: string): boolean => {
        if (!hasCaseTransferPermission) return false;

        const isCoder = roles.includes("coder");
        const isManager = roles.includes("coding_manager");

        if (isManager) return true;

        if (isCoder) {
            return coderRedistributionViews.includes(activeView);
        }

        return true;
    };


    return {
        hasCaseTransferPermission,
        loading,
        error,
        refetch: loadFilterOptions,
        canShowCaseTransferOption
    };
}
